import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { getPlanCta } from "@/lib/planCta";
import { Button } from "@/components/ui/button";
import { History, ArrowRight, Lock, Loader2, AlertTriangle, MapPin } from "lucide-react";

// Condition History entry card — move-out tab, beside Proof Replay / Self-Audit.
// FREE users see the description + upgrade (no API call, so no upsell modal on load).
export function ConditionHistoryCard({ propertyId, archived }) {
  const navigate = useNavigate();
  const { isPremium, trialing, trialEligible, openUpgrade } = useTier();
  const canView = isPremium || trialing;
  const [d, setD] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error
  const cta = getPlanCta({ trialEligible, trialing });

  useEffect(() => {
    if (!canView) return;
    let ok = true;
    api.get(`/properties/${propertyId}/condition-history`)
      .then((r) => { if (ok) { setD(r.data); setState("ok"); } })
      .catch(() => { if (ok) setState("error"); });
    return () => { ok = false; };
  }, [propertyId, canView]);

  const Shell = ({ children, testid, tone = "plain" }) => (
    <div data-testid={testid}
      className={`rounded-2xl border p-5 theme-card ${tone === "plus" ? "border-brand/20 bg-brand/[0.04]" : "border-[#E2E8F0] bg-white"}`}>
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><History size={20} /></span>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );

  // Plus gate — never calls the API for free users, so underlying evidence stays freely accessible.
  if (!canView) {
    return (
      <Shell testid="condition-history-card-locked" tone="plus">
        <h3 className="font-heading text-lg font-bold text-navy flex items-center gap-2">Condition History <Lock size={14} className="text-[#94A3B8]" /></h3>
        <p className="mt-0.5 text-sm text-[#64748B]">See your condition evidence in chronological order across move-in, during tenancy and move-out.</p>
        <Button data-testid="condition-history-upgrade" onClick={() => openUpgrade("condition_history")}
          className="mt-3 h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
          <Lock size={15} className="mr-2" /> {cta.cta}
        </Button>
      </Shell>
    );
  }

  if (state === "loading") return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 flex items-center justify-center theme-card" data-testid="condition-history-card-loading">
      <Loader2 className="animate-spin text-brand" size={18} />
    </div>
  );

  if (state === "error") return (
    <Shell testid="condition-history-card-error">
      <h3 className="font-heading text-lg font-bold text-navy">Condition History</h3>
      <p className="mt-0.5 text-sm text-[#64748B] flex items-center gap-1.5"><AlertTriangle size={14} className="text-amber-500" /> We couldn't load your history right now. Please try again.</p>
      <Button data-testid="condition-history-retry" variant="outline" onClick={() => { setState("loading"); setD(null); api.get(`/properties/${propertyId}/condition-history`).then((r) => { setD(r.data); setState("ok"); }).catch(() => setState("error")); }}
        className="mt-3 h-10 rounded-xl border-[#E2E8F0] text-navy">Retry</Button>
    </Shell>
  );

  const groups = d?.groups || [];
  const excluded = d?.excluded_unconfirmed_count || 0;

  // Needs confirmed locations: nothing groupable yet, but during-tenancy records exist.
  if (groups.length === 0 && excluded > 0) {
    return (
      <Shell testid="condition-history-card-needs-locations">
        <h3 className="font-heading text-lg font-bold text-navy">Condition History</h3>
        <p className="mt-0.5 text-sm text-[#64748B] flex items-start gap-1.5"><MapPin size={14} className="text-amber-500 mt-0.5 shrink-0" /> {excluded} record{excluded === 1 ? "" : "s"} need a confirmed room or location before they can join your history.</p>
      </Shell>
    );
  }

  // Not enough history: fewer than two eligible moments in any single location.
  if (!d?.has_history) {
    return (
      <Shell testid="condition-history-card-empty">
        <h3 className="font-heading text-lg font-bold text-navy">Condition History</h3>
        <p className="mt-0.5 text-sm text-[#64748B]">Add another documented checkup for the same room or location to build its history.</p>
      </Shell>
    );
  }

  const moments = groups.reduce((n, g) => n + g.moments.length, 0);
  return (
    <Shell testid="condition-history-card">
      <h3 className="font-heading text-lg font-bold text-navy flex items-center gap-2">
        Condition History {archived && <span className="text-[10px] font-semibold text-[#64748B] bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">Archived · read-only</span>}
      </h3>
      <p className="mt-0.5 text-sm text-[#64748B] leading-snug">Your condition evidence in chronological order across move-in, during tenancy and move-out.</p>
      <p className="mt-2 text-sm font-semibold text-navy" data-testid="condition-history-card-summary">
        {moments} moments across {groups.length} location{groups.length === 1 ? "" : "s"}
      </p>
      <Button data-testid="condition-history-open" onClick={() => navigate(`/condition-history/${propertyId}`)}
        className="mt-3 h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
        View Condition History <ArrowRight size={15} className="ml-1.5" />
      </Button>
    </Shell>
  );
}
